import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, Link as LinkIcon, Plus, Trash2, Check, Instagram, Facebook, Linkedin, Twitter } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminSocialLinks() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchSocialLinks();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/social-links`);
      setSocialLinks(response.data.links || []);
    } catch (error) {
      toast.error('Failed to fetch social links');
    } finally {
      setLoading(false);
    }
  };

  const updateSocialLinks = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/social-links`,
        { links: socialLinks },
        { withCredentials: true }
      );
      toast.success('Social links updated successfully');
    } catch (error) {
      toast.error('Failed to update social links');
    }
  };

  const handleLinkChange = (index, field, value) => {
    const updated = [...socialLinks];
    updated[index][field] = value;
    setSocialLinks(updated);
  };

  const addNewLink = () => {
    setSocialLinks([...socialLinks, { name: '', url: '', enabled: true }]);
  };

  const removeLink = (index) => {
    const updated = socialLinks.filter((_, i) => i !== index);
    setSocialLinks(updated);
  };

  const getSocialIcon = (name) => {
    const normalizedName = name.toLowerCase();
    if (normalizedName.includes('instagram')) return <Instagram className="h-5 w-5" />;
    if (normalizedName.includes('facebook')) return <Facebook className="h-5 w-5" />;
    if (normalizedName.includes('linkedin')) return <Linkedin className="h-5 w-5" />;
    if (normalizedName.includes('x') || normalizedName.includes('twitter')) return <Twitter className="h-5 w-5" />;
    return <LinkIcon className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <Building2 className="h-6 w-6 text-orange-500" />
                <h1 className="text-xl font-bold text-slate-900">Social Media Links</h1>
              </div>
            </div>
            <span className="text-sm text-slate-600">Hi, {user?.name}</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Manage Social Media Links</h2>
              <p className="text-sm text-slate-600">
                Configure the social media links shown in the footer and Help page
              </p>
            </div>
            <button
              onClick={addNewLink}
              className="btn-accent flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Link
            </button>
          </div>

          <div className="space-y-4 mb-6">
            {socialLinks.map((link, index) => (
              <div key={`social-link-${index}`} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    {getSocialIcon(link.name)}
                  </div>
                  
                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">
                          Platform Name
                        </label>
                        <input
                          type="text"
                          value={link.name}
                          onChange={(e) => handleLinkChange(index, 'name', e.target.value)}
                          placeholder="e.g., Instagram, Facebook"
                          className="input text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">
                          URL
                        </label>
                        <input
                          type="url"
                          value={link.url}
                          onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                          placeholder="https://instagram.com/yourpage"
                          className="input text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={link.enabled}
                          onChange={(e) => handleLinkChange(index, 'enabled', e.target.checked)}
                          className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-slate-700">
                          {link.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                      
                      <button
                        onClick={() => removeLink(index)}
                        className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {socialLinks.length === 0 && (
              <div className="text-center py-12">
                <LinkIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">No social media links configured</p>
                <button
                  onClick={addNewLink}
                  className="btn-accent"
                >
                  Add Your First Link
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              Changes will appear on the footer and Help page
            </p>
            <button
              onClick={updateSocialLinks}
              className="btn-primary flex items-center gap-2"
            >
              <Check className="h-4 w-4" />
              Save Changes
            </button>
          </div>
        </div>

        {/* Preview Section */}
        <div className="mt-8 card">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Preview</h3>
          <div className="bg-slate-900 p-6 rounded-lg">
            <p className="text-slate-400 text-sm mb-4">Footer Preview:</p>
            <div className="flex gap-3">
              {socialLinks.filter(l => l.enabled && l.url).map((link, idx) => (
                <div
                  key={idx}
                  className="w-9 h-9 bg-slate-800 hover:bg-orange-500 rounded-lg flex items-center justify-center transition-colors"
                  title={link.name}
                >
                  {getSocialIcon(link.name)}
                </div>
              ))}
              {socialLinks.filter(l => l.enabled && l.url).length === 0 && (
                <p className="text-slate-500 text-sm">No enabled links to display</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
